import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchAllTournois,
  createTournoi,
  updateTournoi,
  deleteTournoi,
  setActiveTournoi,
  exportTournamentWithBoards,
  importTournamentWithBoards,
  type TournamentExportPayload,
  getAllTournoisList,
  getAllTournoisLoading,
} from "../../../features/tournoi/tournoiSlice";
import { showNotification } from "../../../features/globalNotification/globalNotificationSlice";
import { RegistrationPhase, Tournoi } from "../../../types";
import PageLoader from "../../Base/PageLoader";
import ConfirmDialog from "../../Base/ConfirmDialog";
import TournamentFormDialog from "./TournamentFormDialog";

const PHASE_CHIP: Record<
  RegistrationPhase,
  { label: string; color: "error" | "warning" | "success" | "info" | "default" }
> = {
  closed: { label: "Fermées", color: "error" },
  preRegistration: { label: "Pré-inscription", color: "warning" },
  open: { label: "Ouvertes", color: "success" },
  closedForPlayers: { label: "Joueurs fermés", color: "info" },
  cancelled: { label: "Annulé", color: "error" },
};

const TournamentsManagement: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const tournois = useAppSelector(getAllTournoisList);
  const loading = useAppSelector(getAllTournoisLoading);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedTournoi, setSelectedTournoi] = useState<Tournoi | undefined>();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tournoiToDelete, setTournoiToDelete] = useState<Tournoi | null>(null);

  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [tournoiToActivate, setTournoiToActivate] = useState<Tournoi | null>(
    null
  );
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importMenuAnchorEl, setImportMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [importPhaseMode, setImportPhaseMode] = useState<
    "keepExported" | "resetToClosed"
  >("keepExported");

  useEffect(() => {
    dispatch(fetchAllTournois());
  }, [dispatch]);

  const handleOpenCreate = () => {
    setFormMode("create");
    setSelectedTournoi(undefined);
    setFormOpen(true);
  };

  const handleOpenEdit = (tournoi: Tournoi) => {
    setFormMode("edit");
    setSelectedTournoi(tournoi);
    setFormOpen(true);
  };

  const handleFormSubmit = useCallback(
    async (data: Omit<Tournoi, "id">) => {
      try {
        if (formMode === "create") {
          await dispatch(createTournoi(data)).unwrap();
          dispatch(
            showNotification({
              message: `Tournoi "${data.label}" créé avec succès.`,
              severity: "success",
            })
          );
        } else if (selectedTournoi?.id) {
          await dispatch(
            updateTournoi({ id: selectedTournoi.id, data })
          ).unwrap();
          dispatch(
            showNotification({
              message: `Tournoi "${data.label}" mis à jour.`,
              severity: "success",
            })
          );
        }
        setFormOpen(false);
      } catch {
        dispatch(
          showNotification({
            message: "Une erreur est survenue. Veuillez réessayer.",
            severity: "error",
          })
        );
      }
    },
    [dispatch, formMode, selectedTournoi]
  );

  const handleDeleteRequest = (tournoi: Tournoi) => {
    setTournoiToDelete(tournoi);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tournoiToDelete?.id) return;
    try {
      await dispatch(deleteTournoi(tournoiToDelete.id)).unwrap();
      dispatch(
        showNotification({
          message: `Tournoi "${tournoiToDelete.label}" supprimé.`,
          severity: "success",
        })
      );
    } catch {
      dispatch(
        showNotification({
          message: "Erreur lors de la suppression.",
          severity: "error",
        })
      );
    } finally {
      setDeleteDialogOpen(false);
      setTournoiToDelete(null);
    }
  };

  const handleActivateRequest = (tournoi: Tournoi) => {
    setTournoiToActivate(tournoi);
    setActivateDialogOpen(true);
  };

  const handleActivateConfirm = async () => {
    if (!tournoiToActivate?.id) return;
    try {
      await dispatch(setActiveTournoi(tournoiToActivate.id)).unwrap();
      dispatch(
        showNotification({
          message: `"${tournoiToActivate.label}" est maintenant le tournoi actif. Rechargement…`,
          severity: "success",
        })
      );
      // Recharge l’app pour repartir d’un état cohérent (tableaux, inscriptions, etc.)
      window.setTimeout(() => {
        window.location.reload();
      }, 400);
    } catch {
      dispatch(
        showNotification({
          message: "Erreur lors du changement de tournoi actif.",
          severity: "error",
        })
      );
    } finally {
      setActivateDialogOpen(false);
      setTournoiToActivate(null);
    }
  };

  const handleExportTournament = async (tournoi: Tournoi) => {
    if (!tournoi.id) return;
    try {
      const payload = await dispatch(exportTournamentWithBoards(tournoi.id)).unwrap();
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${tournoi.key}-export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      dispatch(
        showNotification({
          message: `Export du tournoi "${tournoi.label}" généré.`,
          severity: "success",
        })
      );
    } catch {
      dispatch(
        showNotification({
          message: "Erreur lors de l'export du tournoi.",
          severity: "error",
        })
      );
    }
  };

  const handleImportButtonClick = () => {
    setImportMenuAnchorEl(null);
    importInputRef.current?.click();
  };

  const handleOpenImportMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setImportMenuAnchorEl(event.currentTarget);
  };

  const handleSelectImportMode = (
    mode: "keepExported" | "resetToClosed"
  ) => {
    setImportPhaseMode(mode);
    handleImportButtonClick();
  };

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const content = await file.text();
      const parsedPayload = JSON.parse(content) as TournamentExportPayload;
      let candidateKeyOverride: string | undefined;
      for (;;) {
        try {
          await dispatch(
            importTournamentWithBoards({
              payload: parsedPayload,
              keyOverride: candidateKeyOverride,
              registrationPhaseMode: importPhaseMode,
            })
          ).unwrap();
          break;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (!message.includes("La clé")) {
            throw error;
          }
          const fallbackKey =
            candidateKeyOverride && candidateKeyOverride.length > 0
              ? `${candidateKeyOverride}-copie`
              : `${parsedPayload.tournament.key}-copie`;
          const newKey = window.prompt(
            `${message}\n\nSaisissez une autre clé pour poursuivre l'import :`,
            fallbackKey
          );
          if (!newKey || !newKey.trim()) {
            throw new Error("Import annulé (aucune clé alternative fournie).");
          }
          candidateKeyOverride = newKey.trim();
        }
      }
      dispatch(
        showNotification({
          message: "Tournoi importé avec succès.",
          severity: "success",
        })
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error);
      dispatch(
        showNotification({
          message,
          severity: "error",
        })
      );
    }
  };

  if (loading && tournois.length === 0) {
    return <PageLoader message="Chargement des tournois…" />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" component="h1">
          Gestion des tournois
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenImportMenu}
            sx={{
              fontWeight: 600,
              borderWidth: 1.5,
              ...(theme.palette.mode === "dark" && {
                color: "secondary.light",
                borderColor: "secondary.main",
                backgroundColor: "rgba(156, 39, 176, 0.14)",
                "&:hover": {
                  borderColor: "secondary.light",
                  backgroundColor: "rgba(156, 39, 176, 0.24)",
                },
              }),
            }}
          >
            Importer tournoi
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Nouveau tournoi
          </Button>
        </Stack>
      </Stack>
      <input
        ref={importInputRef}
        type="file"
        accept="application/json"
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />
      <Menu
        anchorEl={importMenuAnchorEl}
        open={Boolean(importMenuAnchorEl)}
        onClose={() => setImportMenuAnchorEl(null)}
        PaperProps={{
          sx: theme.palette.mode === "dark"
            ? {
                bgcolor: "grey.900",
                color: "common.white",
                border: "1px solid",
                borderColor: "grey.700",
                "& .MuiMenuItem-root:hover": {
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }
            : undefined,
        }}
      >
        <MenuItem onClick={() => handleSelectImportMode("keepExported")}>
          Importer avec statut du fichier d'export
        </MenuItem>
        <MenuItem onClick={() => handleSelectImportMode("resetToClosed")}>
          Importer en statut initial (Fermées)
        </MenuItem>
      </Menu>

      {tournois.length === 0 ? (
        <Typography color="text.secondary">Aucun tournoi trouvé.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Clé</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="center">Inscriptions</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tournois.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={t.current ? 600 : 400}>
                      {t.label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {t.key}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {t.current ? (
                      <Chip label="Actif" color="success" size="small" />
                    ) : (
                      <Chip label="Inactif" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const phase = t.registrationPhase ?? "closed";
                      const cfg = PHASE_CHIP[phase];
                      return (
                        <Chip label={cfg.label} color={cfg.color} size="small" />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip
                        title={
                          t.current
                            ? "Tournoi déjà actif"
                            : "Définir comme tournoi actif"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            disabled={t.current}
                            onClick={() => handleActivateRequest(t)}
                          >
                            {t.current ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEdit(t)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Exporter JSON (tournoi + tableaux)">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleExportTournament(t)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          t.current
                            ? "Impossible de supprimer le tournoi actif"
                            : "Supprimer"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={t.current}
                            onClick={() => handleDeleteRequest(t)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TournamentFormDialog
        open={formOpen}
        mode={formMode}
        initialData={selectedTournoi}
        allTournois={tournois}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Supprimer le tournoi"
        message={`Êtes-vous sûr de vouloir supprimer le tournoi "${tournoiToDelete?.label}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setTournoiToDelete(null);
        }}
      />

      <ConfirmDialog
        open={activateDialogOpen}
        title="Changer le tournoi actif"
        message={`Voulez-vous définir "${tournoiToActivate?.label}" comme tournoi actif ? Le tournoi actuellement actif sera désactivé.`}
        confirmLabel="Définir actif"
        confirmColor="warning"
        onConfirm={handleActivateConfirm}
        onCancel={() => {
          setActivateDialogOpen(false);
          setTournoiToActivate(null);
        }}
      />
    </Box>
  );
};

export default TournamentsManagement;
